// Flow Sync - Handles synchronization between Drawflow and Livewire
class FlowSync {
    constructor() {
        this.livewireComponent = null;
        this.pendingUpdates = [];
        this.syncInterval = null;
        this.isSyncing = false;
    }

    init(livewireComponentId) {
        // Find the Livewire component
        if (window.Livewire) {
            this.livewireComponent = window.Livewire.find(livewireComponentId);
            this.setupListeners();
            this.startAutoSync();
        } else {
            console.warn('Livewire not found. Flow sync disabled.');
        }
    }

    setupListeners() {
        if (!window.Livewire) return;

        // Listen for Livewire events
        window.Livewire.on('flowBuilder:loadWorkflow', (data) => {
            this.loadWorkflow(data);
        });

        window.Livewire.on('flowBuilder:nodeUpdated', (data) => {
            this.updateNode(data.nodeId, data.updates);
        });

        window.Livewire.on('flowBuilder:refreshCanvas', () => {
            this.refreshCanvas();
        });

        // Listen for Drawflow events (these are set up in flow-builder.js)
        document.addEventListener('flowBuilder:nodeCreated', (e) => {
            this.queueUpdate('createNode', e.detail);
        });

        document.addEventListener('flowBuilder:nodeRemoved', (e) => {
            this.queueUpdate('removeNode', e.detail);
        });

        document.addEventListener('flowBuilder:connectionCreated', (e) => {
            this.queueUpdate('createConnection', e.detail);
        });

        document.addEventListener('flowBuilder:connectionRemoved', (e) => {
            this.queueUpdate('removeConnection', e.detail);
        });
    }

    queueUpdate(action, data) {
        this.pendingUpdates.push({
            action: action,
            data: data,
            timestamp: Date.now()
        });

        // If not already syncing, trigger immediate sync
        if (!this.isSyncing) {
            this.syncNow();
        }
    }

    async syncNow() {
        if (this.isSyncing || this.pendingUpdates.length === 0) return;

        this.isSyncing = true;

        try {
            // Get all pending updates
            const updates = [...this.pendingUpdates];
            this.pendingUpdates = [];

            // Send to Livewire
            if (this.livewireComponent) {
                await this.livewireComponent.call('syncFlowUpdates', updates);
            }
        } catch (error) {
            console.error('Flow sync error:', error);
            // Re-queue failed updates
            this.pendingUpdates.unshift(...updates);
        } finally {
            this.isSyncing = false;
        }
    }

    startAutoSync() {
        // Auto-sync every 5 seconds if there are pending updates
        this.syncInterval = setInterval(() => {
            if (this.pendingUpdates.length > 0) {
                this.syncNow();
            }
        }, 5000);
    }

    stopAutoSync() {
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
            this.syncInterval = null;
        }
    }

    loadWorkflow(workflowData) {
        if (!window.flowBuilder) return;

        // Clear current workflow
        window.flowBuilder.clear();

        // Import workflow data
        if (workflowData.drawflow) {
            window.flowBuilder.importData(workflowData.drawflow);
        } else if (workflowData.steps) {
            // Convert template steps to Drawflow format
            this.convertStepsToDrawflow(workflowData.steps);
        }
    }

    convertStepsToDrawflow(steps) {
        // Convert hierarchical steps to Drawflow nodes
        const processedNodes = new Map();
        let yOffset = 50;

        // Add start node
        const startNode = window.flowBuilder.addNode('start', 400, yOffset, {
            templateType: 'Form',
            templateName: 'Workflow'
        });
        yOffset += 150;

        // Process steps recursively
        const processStep = (step, parentNodeId = null, xOffset = 400) => {
            let nodeType = 'approval';
            let nodeData = {};

            // Determine node type based on step content
            if (step.stepable_type && step.stepable_type.includes('ApprovalStep')) {
                nodeType = 'approval';
                nodeData = {
                    stepId: step.id,
                    approvers: step.content?.approvers || 'Not configured'
                };
            } else if (step.stepable_type && step.stepable_type.includes('JobStep')) {
                nodeType = 'job';
                nodeData = {
                    stepId: step.id,
                    jobClass: step.content?.job_class || 'Not configured'
                };
            } else if (step.operator) {
                nodeType = 'conditional';
                nodeData = {
                    stepId: step.id,
                    condition: `${step.field?.label || 'Field'} ${step.operator} ${step.value}`
                };
            }

            // Create node
            const nodeId = window.flowBuilder.addNode(nodeType, xOffset, yOffset, nodeData);
            processedNodes.set(step.id, nodeId);

            // Connect to parent
            if (parentNodeId !== null) {
                window.flowBuilder.editor.addConnection(
                    parentNodeId,
                    nodeId,
                    'output_1',
                    'input_1'
                );
            }

            // Process children
            if (step.steps && step.steps.length > 0) {
                yOffset += 150;
                const childXSpacing = 200;
                const startX = xOffset - (childXSpacing * (step.steps.length - 1) / 2);

                step.steps.forEach((childStep, index) => {
                    processStep(
                        childStep,
                        nodeId,
                        startX + (childXSpacing * index)
                    );
                });
            }
        };

        // Process all root steps
        if (steps && steps.length > 0) {
            const spacing = 300;
            const startX = 400 - (spacing * (steps.length - 1) / 2);

            steps.forEach((step, index) => {
                processStep(step, startNode, startX + (spacing * index));
                yOffset = 200; // Reset for next root branch
            });
        }
    }

    updateNode(nodeId, updates) {
        if (!window.flowBuilder) return;

        const node = window.flowBuilder.editor.getNodeFromId(nodeId);
        if (!node) return;

        // Update node data
        Object.assign(node.data, updates);

        // Update HTML if needed
        if (updates.html) {
            const nodeElement = document.getElementById(`node-${nodeId}`);
            if (nodeElement) {
                nodeElement.querySelector('.node-body').innerHTML = updates.html;
            }
        }
    }

    refreshCanvas() {
        if (!window.flowBuilder) return;

        // Force redraw of all connections
        window.flowBuilder.editor.updateConnectionNodes('node-1');
    }

    exportWorkflow() {
        if (!window.flowBuilder) return null;

        return {
            drawflow: window.flowBuilder.exportData(),
            timestamp: Date.now()
        };
    }

    getWorkflowStructure() {
        if (!window.flowBuilder) return null;

        const exportData = window.flowBuilder.exportData();
        const structure = {
            nodes: [],
            connections: []
        };

        // Parse Drawflow export format
        if (exportData.drawflow && exportData.drawflow.Home && exportData.drawflow.Home.data) {
            const homeData = exportData.drawflow.Home.data;

            // Extract nodes
            Object.entries(homeData).forEach(([nodeId, nodeData]) => {
                structure.nodes.push({
                    id: nodeId,
                    type: nodeData.name,
                    data: nodeData.data,
                    position: {
                        x: nodeData.pos_x,
                        y: nodeData.pos_y
                    }
                });
            });

            // Extract connections
            Object.entries(homeData).forEach(([nodeId, nodeData]) => {
                if (nodeData.outputs) {
                    Object.entries(nodeData.outputs).forEach(([outputKey, output]) => {
                        if (output.connections) {
                            output.connections.forEach(conn => {
                                structure.connections.push({
                                    source: nodeId,
                                    target: conn.node,
                                    sourceOutput: outputKey,
                                    targetInput: conn.input
                                });
                            });
                        }
                    });
                }
            });
        }

        return structure;
    }

    destroy() {
        this.stopAutoSync();
        this.pendingUpdates = [];
        this.livewireComponent = null;
    }
}

// Initialize and export
window.flowSync = new FlowSync();

// Clean up on page navigation to prevent orphaned intervals
document.addEventListener('livewire:navigating', () => {
    window.flowSync.destroy();
});

export default FlowSync;